import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight,
  Check,
  Activity,
  Pill,
  AlertTriangle,
  Shield,
  Lock,
  Clock,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ===== Elder Care palette (from reference image) =====
   BG_DEEP     #1B1612   — outer deep warm brown
   BG_WARM     #2E251C   — card / surface warm brown
   GOLD        #C59D5D   — primary accent / wordmark sub
   GOLD_LIGHT  #E2C492   — highlight hover
   CREAM       #F3EDE1   — headline
   BODY        #D0C8BE   — body text
   MUTED       #A6A4AA   — secondary text
   LINE        #3E3428   — dividers
   PHONE_BG    #1A1E26   — phone UI bg
   RED_TINT    #6A1A2C   — fall alert bg
   AMBER_TINT  #79481E   — activity alert bg
   GREEN_TINT  #225730   — medication alert bg
*/

export default function ElderCarePage() {
  useEffect(() => {
    document.title = "Aavaa Elder Care — Know the moment something changes.";
    const desc = document.querySelector('meta[name="description"]');
    if (desc)
      desc.setAttribute(
        "content",
        "Aavaa Elder Care: gentle alerts for falls, unusual activity changes, and missed medications — so you always know how your parents are doing at home."
      );
    return () => {
      document.title =
        "Aavaa — Your Child Development Companion | AI-powered daily summaries from your home camera";
    };
  }, []);

  return (
    <div
      className="min-h-screen text-[#D0C8BE]"
      style={{ backgroundColor: "#1B1612" }}
      data-testid="eldercare-page"
    >
      {/* ambient warm glows */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -left-40 w-[620px] h-[620px] rounded-full" style={{ background: "radial-gradient(closest-side, rgba(197,157,93,0.22), transparent)" }} />
        <div className="absolute top-80 -right-40 w-[520px] h-[520px] rounded-full" style={{ background: "radial-gradient(closest-side, rgba(197,157,93,0.13), transparent)" }} />
      </div>

      {/* Nav */}
      <nav className="relative z-20">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-6 flex items-center justify-between">
          <BrandLockup />
          <a
            href="#lead-form"
            data-testid="nav-request-access-btn"
            className="rounded-full px-5 h-10 flex items-center font-manrope text-sm font-medium transition-all border hover:bg-[#C59D5D]/10"
            style={{ borderColor: "#C59D5D", color: "#E2C492" }}
          >
            Request early access
          </a>
        </div>
      </nav>

      {/* ===== HERO ===== */}
      <section className="relative z-10">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-10 sm:pt-16 pb-16 sm:pb-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 animate-fade-up">
            <p className="font-manrope text-xs tracking-[0.25em] uppercase mb-6" style={{ color: "#C59D5D" }}>
              For adults caring for ageing parents
            </p>
            <h1 className="font-playfair text-[#F3EDE1] leading-[1.02] tracking-tight mb-7 text-[44px] sm:text-[60px] lg:text-[72px]">
              Know the moment{" "}
              <em className="italic font-normal" style={{ color: "#C59D5D" }}>
                something changes.
              </em>
            </h1>
            <p className="font-manrope text-lg sm:text-xl leading-relaxed mb-9 max-w-xl" style={{ color: "#D0C8BE" }}>
              Fall alerts. Activity changes. Missed medications. Aavaa quietly watches your parent's day and reaches out only when something genuinely needs you.
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-10">
              <a
                href="#lead-form"
                data-testid="hero-request-access-btn"
                className="rounded-full h-12 px-7 flex items-center font-manrope font-medium text-base transition-all hover:scale-[0.98] active:scale-95"
                style={{ background: "linear-gradient(135deg, #D4B06A, #B48A4E)", color: "#1B1612", boxShadow: "0 12px 30px -12px rgba(197,157,93,0.55)" }}
              >
                Request early access <ArrowRight size={18} className="ml-1.5" />
              </a>
              <Link
                to="/"
                className="font-manrope text-sm transition-colors hover:text-[#F3EDE1]"
                style={{ color: "#A6A4AA" }}
                data-testid="hero-childcare-link"
              >
                Looking for child-care? →
              </Link>
            </div>
            <div className="flex items-center gap-4">
              <Lock size={14} style={{ color: "#A6A4AA" }} />
              <span className="font-manrope text-xs uppercase tracking-[0.18em]" style={{ color: "#A6A4AA" }}>
                Private · Secure · Always
              </span>
            </div>
          </div>

          {/* Hero composition: real photo + floating phone mock overlay */}
          <div className="lg:col-span-6 animate-fade-up" style={{ animationDelay: "0.15s" }}>
            <ElderHeroComposition />
          </div>
        </div>
      </section>

      {/* ===== THREE PILLARS ===== */}
      <section className="relative z-10 border-t" style={{ borderColor: "#3E3428" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
          <div className="max-w-xl mb-12">
            <p className="font-manrope text-xs tracking-[0.25em] uppercase mb-3" style={{ color: "#C59D5D" }}>
              Three things, done right
            </p>
            <h2 className="font-playfair text-[#F3EDE1] text-3xl sm:text-4xl tracking-tight leading-tight">
              Aavaa speaks up only when it matters.
            </h2>
          </div>

          <div className="grid sm:grid-cols-3 gap-5">
            {[
              {
                icon: AlertTriangle,
                tint: "#6A1A2C",
                tintSoft: "rgba(201,65,76,0.12)",
                border: "rgba(201,65,76,0.35)",
                tag: "High",
                title: "Fall detection",
                desc: "If your parent falls, you know within seconds — with the exact clip so you can gauge how serious it is.",
              },
              {
                icon: Activity,
                tint: "#79481E",
                tintSoft: "rgba(201,148,80,0.12)",
                border: "rgba(201,148,80,0.35)",
                tag: "Medium",
                title: "Unusual activity changes",
                desc: "Longer-than-usual time in bed. A skipped morning routine. Aavaa learns their rhythm and flags the drift.",
              },
              {
                icon: Pill,
                tint: "#225730",
                tintSoft: "rgba(111,163,113,0.12)",
                border: "rgba(111,163,113,0.35)",
                tag: "Reminder",
                title: "Medication alerts",
                desc: "A gentle nudge if a dose looks missed, and a heads-up to you if several are missed in a row.",
              },
            ].map((p, i) => (
              <div
                key={i}
                className="rounded-[24px] p-6 border animate-fade-up transition-all"
                style={{ background: "#231A14", borderColor: "#3E3428", animationDelay: `${i * 0.08}s` }}
              >
                <div className="flex items-center justify-between mb-5">
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center border"
                    style={{ background: p.tintSoft, borderColor: p.border }}
                  >
                    <p.icon size={18} style={{ color: "#F3EDE1" }} />
                  </div>
                  <span
                    className="font-manrope text-[10px] tracking-[0.18em] uppercase px-2.5 py-1 rounded-full"
                    style={{ background: p.tintSoft, color: "#F3EDE1", border: `1px solid ${p.border}` }}
                  >
                    {p.tag}
                  </span>
                </div>
                <h3 className="font-playfair text-xl mb-2" style={{ color: "#F3EDE1" }}>
                  {p.title}
                </h3>
                <p className="font-manrope text-sm leading-relaxed" style={{ color: "#A6A4AA" }}>
                  {p.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== QUIET LINE ===== */}
      <section className="relative z-10 border-t" style={{ borderColor: "#3E3428" }}>
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16 sm:py-24 text-center">
          <h2 className="font-playfair italic text-2xl sm:text-3xl leading-relaxed" style={{ color: "#F3EDE1" }}>
            "We live far from them. Aavaa means we never wonder if they're okay."
          </h2>
          <p className="font-manrope text-xs tracking-[0.18em] uppercase mt-4" style={{ color: "#A6A4AA" }}>
            — How we hope you'll feel
          </p>
        </div>
      </section>

      {/* ===== LEAD FORM ===== */}
      <section
        id="lead-form"
        className="relative z-10 border-t scroll-mt-20"
        style={{ borderColor: "#3E3428", background: "#211913" }}
      >
        <div className="max-w-2xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
          <LeadForm />
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="relative z-10 border-t" style={{ borderColor: "#3E3428" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <BrandLockup small />
          <div className="flex items-center gap-5 text-xs font-manrope" style={{ color: "#A6A4AA" }}>
            <Link to="/" className="hover:text-[#F3EDE1] transition-colors" data-testid="footer-childcare-link">Child-care</Link>
            <Link to="/privacy" className="hover:text-[#F3EDE1] transition-colors" data-testid="footer-privacy-link">Privacy</Link>
            <span className="opacity-60">© 2026 Aavaa</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ===================================================== */
/*                    LEAD FORM                          */
/* ===================================================== */
function LeadForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.email && !form.phone) {
      toast.error("Please share an email or a phone number so we can reach you.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/waitlist`, { ...form, category: "eldercare" });
      setSubmitted(true);
      toast.success("Thanks — we'll reach out soon.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="text-center animate-fade-up" data-testid="eldercare-form-success">
        <div
          className="w-14 h-14 rounded-full mx-auto mb-5 flex items-center justify-center"
          style={{ background: "rgba(197,157,93,0.15)", border: "1px solid rgba(197,157,93,0.4)" }}
        >
          <Check size={26} style={{ color: "#E2C492" }} strokeWidth={2} />
        </div>
        <h3 className="font-playfair text-3xl mb-3" style={{ color: "#F3EDE1" }}>
          We'll reach out soon.
        </h3>
        <p className="font-manrope text-base leading-relaxed max-w-md mx-auto" style={{ color: "#A6A4AA" }}>
          Thank you for trusting us with this moment. One of the founders will be in touch personally to walk you through how Aavaa can help.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="eldercare-form">
      <div className="text-center mb-8">
        <p className="font-manrope text-xs tracking-[0.25em] uppercase mb-3" style={{ color: "#C59D5D" }}>
          Early access
        </p>
        <h2 className="font-playfair text-3xl sm:text-4xl leading-tight mb-3" style={{ color: "#F3EDE1" }}>
          Reserve your spot.
        </h2>
        <p className="font-manrope text-sm" style={{ color: "#A6A4AA" }}>
          Leave an email or a phone number. One of us will call you back personally.
        </p>
      </div>

      <div>
        <label htmlFor="ec-name" className="font-manrope text-[10px] tracking-[0.2em] uppercase mb-2 block" style={{ color: "#A6A4AA" }}>
          Your name
        </label>
        <input
          id="ec-name"
          data-testid="eldercare-name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="e.g. Rohan"
          className="w-full rounded-xl h-12 px-4 font-manrope text-base transition-all"
          style={{ background: "#1B1612", color: "#F3EDE1", border: "1px solid #3E3428", outline: "none" }}
          onFocus={(e) => (e.target.style.borderColor = "#C59D5D")}
          onBlur={(e) => (e.target.style.borderColor = "#3E3428")}
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="ec-email" className="font-manrope text-[10px] tracking-[0.2em] uppercase mb-2 block" style={{ color: "#A6A4AA" }}>
            Email
          </label>
          <input
            id="ec-email"
            data-testid="eldercare-email"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="you@email.com"
            className="w-full rounded-xl h-12 px-4 font-manrope text-base transition-all"
            style={{ background: "#1B1612", color: "#F3EDE1", border: "1px solid #3E3428", outline: "none" }}
            onFocus={(e) => (e.target.style.borderColor = "#C59D5D")}
            onBlur={(e) => (e.target.style.borderColor = "#3E3428")}
          />
        </div>
        <div>
          <label htmlFor="ec-phone" className="font-manrope text-[10px] tracking-[0.2em] uppercase mb-2 block" style={{ color: "#A6A4AA" }}>
            Phone
          </label>
          <input
            id="ec-phone"
            data-testid="eldercare-phone"
            type="tel"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="+91 9XXXX XXXXX"
            className="w-full rounded-xl h-12 px-4 font-manrope text-base transition-all"
            style={{ background: "#1B1612", color: "#F3EDE1", border: "1px solid #3E3428", outline: "none" }}
            onFocus={(e) => (e.target.style.borderColor = "#C59D5D")}
            onBlur={(e) => (e.target.style.borderColor = "#3E3428")}
          />
        </div>
      </div>

      <p className="font-manrope text-xs" style={{ color: "rgba(166,164,170,0.7)" }}>
        Please share at least one — email or phone. We'll never spam you. See our{" "}
        <Link
          to="/privacy"
          className="underline decoration-dotted underline-offset-4 transition-colors"
          style={{ color: "#C59D5D" }}
          data-testid="eldercare-form-privacy-link"
        >
          privacy policy
        </Link>
        .
      </p>

      <button
        data-testid="eldercare-submit"
        type="submit"
        disabled={submitting}
        className="w-full rounded-full h-12 font-manrope font-medium text-base transition-all hover:scale-[0.98] active:scale-95 flex items-center justify-center gap-2"
        style={{ background: "linear-gradient(135deg, #D4B06A, #B48A4E)", color: "#1B1612" }}
      >
        {submitting ? "Sending..." : "Request early access"}
        {!submitting && <ArrowRight size={18} />}
      </button>
    </form>
  );
}

/* ===================================================== */
/*                    BRAND LOCKUP                       */
/* ===================================================== */
function BrandLockup({ small = false }) {
  return (
    <div className="flex items-center gap-3" data-testid="eldercare-brand-lockup">
      <div
        className="rounded-lg flex items-center justify-center"
        style={{
          width: small ? 28 : 36,
          height: small ? 28 : 36,
          background: "linear-gradient(135deg, #D4B06A, #A37A40)",
          boxShadow: "0 4px 14px -4px rgba(197,157,93,0.4)",
        }}
      >
        <span
          className="font-playfair italic"
          style={{ color: "#1B1612", fontSize: small ? 18 : 22, fontWeight: 500, lineHeight: 1 }}
        >
          A
        </span>
      </div>
      <div className="flex flex-col leading-none">
        <span className="font-playfair" style={{ color: "#F3EDE1", fontSize: small ? 18 : 22, fontWeight: 500 }}>
          aavaa
        </span>
        <span className="font-manrope uppercase mt-0.5" style={{ color: "#C59D5D", fontSize: small ? 8 : 9, letterSpacing: "0.25em", fontWeight: 500 }}>
          Elder Care
        </span>
      </div>
    </div>
  );
}

/* ===================================================== */
/*        HERO COMPOSITION: real portrait + phone         */
/* ===================================================== */
function ElderHeroComposition() {
  return (
    <div className="relative mx-auto w-full max-w-[560px]" data-testid="eldercare-hero-composition">
      {/* ambient warm glow behind */}
      <div
        className="absolute -inset-10 rounded-[60px] -z-0 blur-3xl"
        style={{ background: "radial-gradient(closest-side, rgba(197,157,93,0.35), transparent)" }}
      />

      {/* Portrait card */}
      <div
        className="relative rounded-[32px] overflow-hidden"
        style={{
          boxShadow: "0 40px 80px -30px rgba(0,0,0,0.7), 0 0 0 1px rgba(197,157,93,0.18)",
        }}
      >
        <img
          src="/images/eldercare-hero.jpg"
          alt="An Indian elder father reading his morning newspaper in soft natural light — the kind of quiet moment Aavaa helps you stay close to."
          loading="eager"
          className="w-full h-auto block aspect-square object-cover"
          style={{ filter: "brightness(0.96) contrast(1.04) saturate(0.98)" }}
        />
        {/* warm gradient vignette to blend with page */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(27,22,18,0) 55%, rgba(27,22,18,0.45) 88%, rgba(27,22,18,0.78) 100%)",
          }}
        />
        {/* soft warm light overlay from top-left (matches natural light in the photo) */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at top left, rgba(197,157,93,0.22), transparent 55%)",
            mixBlendMode: "soft-light",
          }}
        />
      </div>

      {/* Floating "Live" badge top-left of portrait */}
      <div
        className="absolute top-4 left-4 flex items-center gap-1.5 rounded-full px-3 py-1.5"
        style={{
          background: "rgba(27,22,18,0.75)",
          border: "1px solid rgba(197,157,93,0.35)",
          backdropFilter: "blur(10px)",
        }}
      >
        <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#6FA371" }} />
        <span className="font-manrope text-[10px] uppercase tracking-[0.18em]" style={{ color: "#E2C492" }}>
          Aavaa is watching
        </span>
      </div>

      {/* Floating phone mock overlapping bottom-right of portrait */}
      <div
        className="absolute -bottom-8 -right-4 sm:-right-8 w-[52%] max-w-[220px] hidden sm:block"
        style={{ filter: "drop-shadow(0 30px 50px rgba(0,0,0,0.55))" }}
      >
        <CompactPhoneMock />
      </div>

      {/* On mobile — show the phone mock below instead */}
      <div className="block sm:hidden mt-8">
        <AlertPhoneMock />
      </div>
    </div>
  );
}

/* Compact phone version used for hero overlay (just 2 alerts for cleaner composition) */
function CompactPhoneMock() {
  return (
    <div
      className="rounded-[28px] p-2"
      style={{
        background: "#0F0D0B",
        boxShadow: "0 0 0 1px rgba(197,157,93,0.2)",
      }}
    >
      <div className="rounded-[22px] overflow-hidden" style={{ background: "#1A1E26" }}>
        <div className="flex items-center justify-between px-3 pt-2 pb-1 text-[8px] font-manrope" style={{ color: "#A6A4AA" }}>
          <span>9:41 AM</span>
          <span className="opacity-70">●●● Wi-Fi</span>
        </div>
        <div className="px-3 pt-1 pb-3">
          <p className="font-playfair text-[13px]" style={{ color: "#FFFFFF" }}>
            Hello, Ananya
          </p>
          <p className="font-manrope text-[8px] mt-0.5" style={{ color: "#A6A4AA" }}>
            Here's your parent's summary
          </p>
        </div>
        <div className="px-2 pb-3 space-y-1.5">
          <CompactAlert tint="#6A1A2C" border="rgba(201,65,76,0.45)" chipBg="rgba(201,65,76,0.22)" chip="High" icon={AlertTriangle} title="Fall detected" sub="10:24 AM" />
          <CompactAlert tint="#79481E" border="rgba(201,148,80,0.45)" chipBg="rgba(201,148,80,0.22)" chip="Med" icon={Activity} title="Still in bed" sub="7h 42m" />
          <CompactAlert tint="#225730" border="rgba(111,163,113,0.45)" chipBg="rgba(111,163,113,0.22)" chip="Rem." icon={Pill} title="Missed dose" sub="8:00 AM" />
        </div>
      </div>
    </div>
  );
}

function CompactAlert({ tint, border, chipBg, chip, icon: Icon, title, sub }) {
  return (
    <div className="rounded-xl p-2 flex items-center gap-2" style={{ background: tint, border: `1px solid ${border}` }}>
      <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: chipBg }}>
        <Icon size={11} style={{ color: "#FFFFFF" }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-manrope text-[10px] font-semibold leading-tight truncate" style={{ color: "#FFFFFF" }}>{title}</p>
        <p className="font-manrope text-[8px] opacity-80 leading-tight" style={{ color: "#C2C2C5" }}>{sub}</p>
      </div>
      <span className="font-manrope text-[7px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded-full flex-shrink-0" style={{ background: chipBg, color: "#FFFFFF", border: `1px solid ${border}` }}>
        {chip}
      </span>
    </div>
  );
}

/* ===================================================== */
/*                   PHONE MOCK                          */
/* ===================================================== */
function AlertPhoneMock() {
  return (
    <div className="relative mx-auto w-full max-w-[320px]" data-testid="eldercare-phone-mock">
      {/* ambient glow */}
      <div
        className="absolute -inset-8 rounded-[56px] -z-0 blur-2xl"
        style={{ background: "radial-gradient(closest-side, rgba(197,157,93,0.35), transparent)" }}
      />

      <div
        className="relative rounded-[40px] p-3"
        style={{
          background: "#0F0D0B",
          boxShadow: "0 40px 80px -20px rgba(0,0,0,0.6), 0 0 0 1px rgba(197,157,93,0.12)",
        }}
      >
        <div className="rounded-[30px] overflow-hidden" style={{ background: "#1A1E26" }}>
          {/* status bar */}
          <div className="flex items-center justify-between px-5 pt-4 pb-2 text-[10px] font-manrope" style={{ color: "#A6A4AA" }}>
            <span>9:41 AM</span>
            <div className="flex items-center gap-1 opacity-70">
              <span>●●●</span>
              <span>Wi-Fi</span>
            </div>
          </div>

          {/* greeting */}
          <div className="px-5 pt-2 pb-5">
            <p className="font-playfair text-xl" style={{ color: "#FFFFFF" }}>
              Hello, Ananya
            </p>
            <p className="font-manrope text-[11px] mt-1" style={{ color: "#A6A4AA" }}>
              Here's your parent's summary
            </p>
          </div>

          <div className="px-4 pb-5 space-y-3">
            <AlertCard
              tint="#6A1A2C"
              border="rgba(201,65,76,0.45)"
              chipBg="rgba(201,65,76,0.22)"
              chip="High"
              icon={AlertTriangle}
              iconBg="rgba(201,65,76,0.22)"
              title="Fall detected"
              sub="10:24 AM · Living Room"
            />
            <AlertCard
              tint="#79481E"
              border="rgba(201,148,80,0.45)"
              chipBg="rgba(201,148,80,0.22)"
              chip="Medium"
              icon={Activity}
              iconBg="rgba(201,148,80,0.22)"
              title="Activity change"
              sub="Still in bed · 7h 42m"
            />
            <AlertCard
              tint="#225730"
              border="rgba(111,163,113,0.45)"
              chipBg="rgba(111,163,113,0.22)"
              chip="Reminder"
              icon={Pill}
              iconBg="rgba(111,163,113,0.22)"
              title="Medication alert"
              sub="Medicine missed · 8:00 AM"
            />
          </div>

          {/* footer bar */}
          <div className="flex items-center justify-center py-3 border-t" style={{ borderColor: "#2A2F38" }}>
            <div className="flex items-center gap-1.5">
              <Shield size={11} style={{ color: "#A6A4AA" }} />
              <span className="font-manrope text-[9px] uppercase tracking-[0.18em]" style={{ color: "#A6A4AA" }}>
                Private · Secure · Always
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* small glowing pulse to indicate real-time */}
      <div
        className="absolute -top-3 -right-3 flex items-center gap-1.5 rounded-full px-3 py-1.5"
        style={{ background: "rgba(27,22,18,0.85)", border: "1px solid #3E3428", backdropFilter: "blur(8px)" }}
      >
        <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#6FA371" }} />
        <span className="font-manrope text-[10px]" style={{ color: "#D0C8BE" }}>Live</span>
      </div>
    </div>
  );
}

function AlertCard({ tint, border, chipBg, chip, icon: Icon, iconBg, title, sub }) {
  return (
    <div
      className="rounded-2xl p-3.5 flex items-center gap-3 animate-fade-up"
      style={{ background: tint, border: `1px solid ${border}` }}
    >
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: iconBg }}>
        <Icon size={16} style={{ color: "#FFFFFF" }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-manrope text-[13px] font-semibold" style={{ color: "#FFFFFF" }}>
          {title}
        </p>
        <p className="font-manrope text-[10px] mt-0.5" style={{ color: "#C2C2C5" }}>
          {sub}
        </p>
      </div>
      <span
        className="font-manrope text-[9px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full flex-shrink-0"
        style={{ background: chipBg, color: "#FFFFFF", border: `1px solid ${border}` }}
      >
        {chip}
      </span>
    </div>
  );
}
